import type { ReactNode } from 'react';
import React, { useEffect, useState } from 'react';

import { deviceModelService } from '@/service/device-model';

/** 请求去重缓存：同 model 多次调用共享同一个 Promise */
const inflightCache = new Map<string, Promise<string | null>>();

/** 拼装展示后缀：品牌 + 营销名称（如 "Apple iPhone 13 Pro"），无品牌时仅展示营销名称 */
function formatDisplaySuffix(item: { brand?: string | null; marketingName: string }): string {
  return [item.brand, item.marketingName].filter(Boolean).join(' ');
}

/**
 * 获取单个型号的展示名称（品牌 + 营销名称，内置请求去重缓存）
 */
function fetchMarketingName(model: string): Promise<string | null> {
  const cached = inflightCache.get(model);
  if (cached) return cached;

  const promise = deviceModelService
    .getMarketingNameByModel([model])
    .then((list) => {
      const item = list[0];
      if (item?.found && item?.marketingName) {
        return formatDisplaySuffix({ brand: item.brand, marketingName: item.marketingName });
      }
      return null;
    });

  inflightCache.set(model, promise);
  return promise;
}

interface UseMarketingNameResult {
  /** 格式化的展示 JSX：型号 + 灰色市场名称（若有） */
  displayLabel: ReactNode;
  /** 是否加载中 */
  loading: boolean;
  /** 市场名称（无则 null） */
  marketingName: string | null;
}

/**
 * 单型号营销名称查询 hook
 *
 * 请求去重：同一 model 多次渲染只发一次 API 请求。
 * 非 iOS 平台也可能返回 marketingName（Android 有营销名称映射），所有平台统一走 API。
 */
export function useMarketingName(model: string | null | undefined): UseMarketingNameResult {
  const [marketingName, setMarketingName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!model) {
      setMarketingName(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetchMarketingName(model).then((name) => {
      if (cancelled) return;
      setMarketingName(name);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [model]);

  // 型号 + 市场名称灰色
  let displayLabel: ReactNode = '-';
  if (model) {
    if (marketingName) {
      displayLabel = (
        <span>
          <span>{model}</span>
          <span style={{ color: '#999' }}>{` (${marketingName})`}</span>
        </span>
      );
    } else {
      displayLabel = <span>{model}</span>;
    }
  }

  return { displayLabel, loading, marketingName };
}
